import { create } from 'zustand';

export interface ConfirmOptions {
  /** The question, naming the object: `Delete "Diwali offer"?` */
  title: string;
  /** What happens next, or what cannot be undone. */
  description?: string;
  /** Verb-first action label. Defaults to common.confirm. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red action (default true). */
  destructive?: boolean;
}

interface ConfirmRequest extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

interface ConfirmState {
  request: ConfirmRequest | null;
  ask: (options: ConfirmOptions) => Promise<boolean>;
  settle: (ok: boolean) => void;
}

export const useConfirmStore = create<ConfirmState>((set, get) => ({
  request: null,
  ask: (options) =>
    new Promise<boolean>((resolve) => {
      // One question at a time: a newer one cancels the one still open.
      get().request?.resolve(false);
      set({ request: { ...options, resolve } });
    }),
  settle: (ok) => {
    const { request } = get();
    if (!request) return;
    set({ request: null });
    request.resolve(ok);
  },
}));

/**
 * Ask a named confirmation and wait for the answer — the in-app replacement
 * for `window.confirm`. Resolves true on the action, false on Cancel, Escape
 * or the backdrop. Rendered by `ConfirmDialogHost` (main.tsx).
 */
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  return useConfirmStore.getState().ask(options);
}

/**
 * Existing confirmation strings are one sentence pair: "Disconnect X? Access
 * is revoked and …". Without a separate description, the question becomes the
 * title and the rest the description, so a paragraph is never set as a 20px
 * headline. Splits after the first question mark (Latin ?, Arabic/Urdu ؟, the
 * Greek question mark U+037E) that is followed by more text; otherwise the
 * whole string is the title.
 */
export function splitQuestion(text: string): { title: string; rest: string | null } {
  const match = /^(.+?[?؟\u037E])\s+(\S[\s\S]*)$/u.exec(text.trim());
  if (!match?.[1] || !match[2]) return { title: text, rest: null };
  return { title: match[1], rest: match[2] };
}
